import { Box } from '@react-three/drei'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  getFloorColor,
  getFloorNormalMapUrl,
  getFloorOpacity,
  getFloorRoughnessMapUrl,
  getFloorTextureUrl,
  getFloorTileSize,
} from 'higgins-domain'
import { useEffect, useMemo, useRef } from 'react'
import { type IUniform, RepeatWrapping } from 'three'

import { useResourceQueryFactories } from '@salsita-ai/platform-project-sdk/configurator'

import { configuratorInterface } from '../systems/configurator-interface.js'

const FLOOR_COLOR = '#ffffff'
const FLOOR_OPACITY = 0.8

export const Floor = () => {
  const selectedKey = configuratorInterface.useRootEntitySelector(root => root.floorTexture, [])
  const url = getFloorTextureUrl(selectedKey)
  const normalMapUrl = getFloorNormalMapUrl(selectedKey)
  const roughnessMapUrl = getFloorRoughnessMapUrl(selectedKey)
  const tileSize = getFloorTileSize(selectedKey)
  const color = getFloorColor(selectedKey)
  const opacity = getFloorOpacity(selectedKey)

  if (!url) return null

  return (
    <TexturedFloor
      url={url}
      normalMapUrl={normalMapUrl}
      roughnessMapUrl={roughnessMapUrl}
      tileSize={tileSize}
      color={color}
      opacity={opacity}
    />
  )
}

const TexturedFloor = ({
  url,
  normalMapUrl,
  roughnessMapUrl,
  tileSize,
  color,
  opacity,
}: {
  url: string
  normalMapUrl?: string
  roughnessMapUrl?: string
  tileSize?: number
  color?: string
  opacity?: number
}) => {
  const { createTextureQuery } = useResourceQueryFactories()
  const { data: texture } = useSuspenseQuery(createTextureQuery(url))

  // Load normal map if available
  const { data: normalMap } = useQuery({
    ...createTextureQuery(normalMapUrl!),
    enabled: !!normalMapUrl,
  })

  // Load roughness map if available
  const { data: roughnessMap } = useQuery({
    ...createTextureQuery(roughnessMapUrl!),
    enabled: !!roughnessMapUrl,
  })

  const rootEntity = configuratorInterface.useRootEntityState()

  const FLOOR_SIZE_X_MM = 1000
  const FLOOR_SIZE_Z_MM = 6000
  const TILE_SIZE_MM = tileSize ?? 800

  const floorWidthX =
    rootEntity.$boundingBoxMaxX && rootEntity.$boundingBoxMinX
      ? rootEntity.$boundingBoxMaxX - rootEntity.$boundingBoxMinX
      : FLOOR_SIZE_X_MM
  const floorSizeX = floorWidthX + 7000
  const position = useMemo<[number, number, number]>(
    () => [(rootEntity.$boundingBoxMinX ?? 0) + floorWidthX / 2, -1, 0],
    [rootEntity.$boundingBoxMinX, floorWidthX]
  )

  const repeatX = floorSizeX / TILE_SIZE_MM
  const repeatZ = FLOOR_SIZE_Z_MM / TILE_SIZE_MM

  const configuredTextures = useMemo(() => {
    // Configure main texture
    texture.wrapT = RepeatWrapping
    texture.wrapS = RepeatWrapping
    texture.repeat.set(repeatX, repeatZ)

    // Configure normal map if available
    if (normalMap) {
      normalMap.wrapT = RepeatWrapping
      normalMap.wrapS = RepeatWrapping
      normalMap.repeat.set(repeatX, repeatZ)
    }

    // Configure roughness map if available
    if (roughnessMap) {
      roughnessMap.wrapT = RepeatWrapping
      roughnessMap.wrapS = RepeatWrapping
      roughnessMap.repeat.set(repeatX, repeatZ)
    }

    return { texture, normalMap, roughnessMap }
  }, [texture, normalMap, roughnessMap, repeatX, repeatZ])

  // Store shader uniforms in a ref so we can update them without recreating the material
  const uniformsRef = useRef<{
    uCenterX: IUniform<number>
    uCenterZ: IUniform<number>
    uRadiusX: IUniform<number>
    uRadiusZ: IUniform<number>
  } | null>(null)

  // Update uniforms when position or size changes
  useEffect(() => {
    if (uniformsRef.current) {
      uniformsRef.current.uCenterX.value = position[0]
      uniformsRef.current.uCenterZ.value = position[2]
      uniformsRef.current.uRadiusX.value = floorSizeX / 2
      uniformsRef.current.uRadiusZ.value = FLOOR_SIZE_Z_MM / 2
    }
  }, [position, floorSizeX, FLOOR_SIZE_Z_MM])

  return (
    <Box args={[floorSizeX, 0, FLOOR_SIZE_Z_MM]} position={position}>
      <meshStandardMaterial
        color={color ?? FLOOR_COLOR}
        map={configuredTextures.texture}
        normalMap={configuredTextures.normalMap}
        roughnessMap={configuredTextures.roughnessMap}
        roughness={1}
        metalness={0}
        opacity={opacity ?? FLOOR_OPACITY} // makes the floor a bit lighter
        transparent
        // Elliptical shader to fade out the floor to its corners
        onBeforeCompile={shader => {
          // Create uniforms for dynamic values
          const uniforms = {
            uCenterX: { value: position[0] },
            uCenterZ: { value: position[2] },
            uRadiusX: { value: floorSizeX / 2 },
            uRadiusZ: { value: FLOOR_SIZE_Z_MM / 2 },
          }
          uniformsRef.current = uniforms
          shader.uniforms['uCenterX'] = uniforms.uCenterX
          shader.uniforms['uCenterZ'] = uniforms.uCenterZ
          shader.uniforms['uRadiusX'] = uniforms.uRadiusX
          shader.uniforms['uRadiusZ'] = uniforms.uRadiusZ

          // Add world position varying to both shaders
          shader.vertexShader = shader.vertexShader
            .replace('#include <common>', '#include <common>\nvarying vec3 vWorldPosition;')
            .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWorldPosition = worldPosition.xyz;')
          // Add elliptical fade effect to fragment shader, centered on the floor position
          shader.fragmentShader = shader.fragmentShader
            .replace(
              '#include <common>',
              `#include <common>
            varying vec3 vWorldPosition;
            uniform float uCenterX;
            uniform float uCenterZ;
            uniform float uRadiusX;
            uniform float uRadiusZ;`
            )
            .replace(
              '#include <map_fragment>',
              `#include <map_fragment>
            float innerRadiusX = uRadiusX * 0.4; // Floor fades out from XX% of max radius
            float innerRadiusZ = uRadiusZ * 0.1; // Floor fades out from XX% of max radius
            float ellipseDistance = length(vec2((vWorldPosition.x - uCenterX) / uRadiusX, (vWorldPosition.z - uCenterZ) / uRadiusZ));
            float innerEllipseDistance = length(vec2(innerRadiusX / uRadiusX, innerRadiusZ / uRadiusZ));
            diffuseColor.a *= 1.0 - smoothstep(innerEllipseDistance, 1.0, ellipseDistance);`
            )
        }}
      />
    </Box>
  )
}
